import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Stack,
  Card,
  CardContent,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import FollowUpIcon from "@mui/icons-material/Update";
import PresentationIcon from "@mui/icons-material/Slideshow";
import DescriptionIcon from "@mui/icons-material/Description";
import { useTheme } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import CreateTaskModal from "../../Leads_V2/Followup/CreateTaskModal";
import { getFollowupTasksApi, updateFollowupTaskApi } from "../../../api/followupTasks.api";

const MotionCard = motion.create ? motion.create(Card) : motion(Card);
const MotionBox = motion.create ? motion.create(Box) : motion(Box);

const taskTypeIcons = {
  Call: PhoneIcon,
  Email: EmailIcon,
  Meeting: MeetingRoomIcon,
  "Follow-up": FollowUpIcon,
  Demo: PresentationIcon,
  Proposal: DescriptionIcon,
};

const Tasks = () => {
  const theme = useTheme();
  const { activeLead } = useOutletContext();

  const [open, setOpen] = useState(false);
  const [followupTasks, setFollowupTasks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch followup tasks for this lead
  useEffect(() => {
    if (activeLead?._id) {
      fetchFollowupTasks();
    }
  }, [activeLead?._id]);

  const fetchFollowupTasks = async () => {
    try {
      setLoading(true);
      const response = await getFollowupTasksApi();
      // Filter tasks for this specific lead
      const leadTasks = response.data?.tasks?.filter(
        (task) => task.lead?._id === activeLead._id || task.lead === activeLead._id
      ) || [];
      setFollowupTasks(leadTasks);
    } catch (error) {
      console.error("Error fetching followup tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskCreate = (taskData) => {
    // Task is already created in the database by the modal
    // Refresh the tasks list
    fetchFollowupTasks();
    setOpen(false);
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      await updateFollowupTaskApi(taskId, { status: newStatus });
      // Refresh tasks after update
      fetchFollowupTasks();
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  const formatScheduledDate = (task) => {
    if (!task.scheduledDate) return null;
    const scheduled = new Date(task.scheduledDate);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const scheduledDay = new Date(scheduled.getFullYear(), scheduled.getMonth(), scheduled.getDate());

    if (scheduledDay.getTime() === today.getTime()) {
      return `Today, ${task.preferredTime}`;
    } else if (scheduledDay.getTime() === tomorrow.getTime()) {
      return `Tomorrow, ${task.preferredTime}`;
    }
    return `${scheduled.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}, ${task.preferredTime}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return { bg: "#FEE2E2", color: "#DC2626" };
      case "med":
        return { bg: "#FEF3C7", color: "#D97706" };
      default:
        return { bg: "#F1F5F9", color: "#64748B" };
    }
  };

  return (
    <MotionCard
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      sx={{
        borderRadius: 4,
        bgcolor: "#FFFFFF",
        boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        border: "1px solid #F1F5F9",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Tasks
          </Typography>

          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => setOpen(true)}
            sx={{
              px: 2.5,
              py: 1,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2.5,
              textTransform: "none",
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              color: "#FFFFFF",
              boxShadow: "0 4px 14px primary.main",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                // boxShadow: "0 6px 20px rgba(124, 58, 237, 0.35)",
              },
            }}
          >
            Add Task
          </Button>
        </Stack>

        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ py: 5 }}
          >
            <CircularProgress size={32} />
          </Stack>
        ) : followupTasks.length === 0 ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              py: 5,
              borderRadius: 3,
              bgcolor: "#FAFAFA",
              border: "1px dashed #E2E8F0",
            }}
          >
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#64748B",
                textAlign: "center",
              }}
            >
              No tasks added yet
            </Typography>

            <Typography
              sx={{
                fontSize: 12,
                mt: 0.5,
                color: "#94A3B8",
                textAlign: "center",
              }}
            >
              Click the Add Task button to create one
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <AnimatePresence>
              {followupTasks.map((task, idx) => {
                const isCompleted = task.status === "completed";
                const TaskIcon = taskTypeIcons[task.taskType] || AssignmentIcon;
                const priorityStyle = getPriorityColor(task.priority);

                return (
                  <MotionBox
                    key={task._id || idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    whileHover={{ scale: 1.01 }}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "#FAFAFA",
                      border: "1px solid #F1F5F9",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        bgcolor: "#F8FAFC",
                        borderColor: "#E2E8F0",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2.5,
                        bgcolor: `${theme.palette.primary.main}1F`,
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                      }}
                    >
                      <TaskIcon sx={{ fontSize: 22 }} />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: isCompleted ? "#94A3B8" : "#1E293B",
                            textDecoration: isCompleted ? "line-through" : "none",
                            lineHeight: 1.4,
                          }}
                        >
                          {task.taskType}
                        </Typography>
                        <Chip
                          label={task.priority?.toUpperCase() || "LOW"}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: priorityStyle.bg,
                            color: priorityStyle.color,
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      </Stack>

                      {task.description && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#64748B",
                            mb: 0.5,
                          }}
                        >
                          {task.description}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={2} mt={0.5}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "#94A3B8",
                          }}
                        >
                          {formatScheduledDate(task)}
                        </Typography>
                        {task.assignedToName && task.assignedToName !== "Unassigned" && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: "#94A3B8",
                            }}
                          >
                            Assigned to: {task.assignedToName}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    <Checkbox
                      checked={isCompleted}
                      onChange={() =>
                        handleStatusUpdate(
                          task._id,
                          isCompleted ? "pending" : "completed"
                        )
                      }
                      sx={{
                        p: 0.5,
                        color: "#E2E8F0",
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                        "& .MuiSvgIcon-root": {
                          fontSize: 24,
                        },
                      }}
                    />
                  </MotionBox>
                );
              })}
            </AnimatePresence>
          </Stack>
        )}

        <CreateTaskModal
          open={open}
          onClose={() => setOpen(false)}
          onSubmit={handleTaskCreate}
          preSelectedLead={
            activeLead
              ? {
                  _id: activeLead._id,
                  client_name: activeLead.client_name,
                  contact: activeLead.contact,
                  email: activeLead.email,
                }
              : null
          }
        />
      </CardContent>
    </MotionCard>
  );
};

export default Tasks;
